<script setup>
import { ref, computed } from 'vue'
import { useGlobalStore } from '@stores/global'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

const global = useGlobalStore()
const props = defineProps(['modelValue', 'label', 'placeholder', 'error', 'icon'])
const emit = defineEmits(['update:modelValue'])

const dateValue = computed({
	get() {
		return props.modelValue
	},
	set(value) {
		emit('update:modelValue', value)
	},
})
</script>

<template>
	<div class="flex w-full flex-col gap-2 normal-case">
		<label class="text-sm text-crowns-off-white" v-if="label">
			{{ label }}
		</label>

		<div class="relative flex w-full">
			<i :class="'absolute left-4 top-1/2 -translate-y-1/2 text-sm text-neutral-500 ' + icon"></i>
			<VueDatePicker v-model="dateValue" v-bind="$attrs" :placeholder="placeholder" :label="label" :error="error" :enableTimePicker="false" />
		</div>
		<p class="text-xs text-red-500" v-if="error">
			{{ error }}
		</p>
	</div>
</template>

<style>
.dp__theme_light {
	--dp-background-color: #111;
	--dp-text-color: #d8d8d8;
	--dp-hover-color: #1b1b1b;
	--dp-hover-text-color: #d8d8d8;
	--dp-hover-icon-color: #959595;
	--dp-primary-color: #b0b0b0;
	--dp-primary-disabled-color: #6bacea;
	--dp-primary-text-color: #080808;
	--dp-secondary-color: #656565;
	--dp-border-color: transparent;
	--dp-menu-border-color: #1b1b1b;
	--dp-border-color-hover: transparent;
	--dp-border-color-focus: #e5e5e5;
	--dp-disabled-color: #000;
	--dp-scroll-bar-background: #f3f4f8;
	--dp-scroll-bar-color: #959595;
	--dp-success-color: #76d275;
	--dp-success-color-disabled: #a3d9b1;
	--dp-icon-color: #959595;
	--dp-danger-color: #ff6f60;
	--dp-marker-color: #ff6f60;
	--dp-tooltip-color: #fafafa;
	--dp-disabled-color-text: #8e8e8e;
	--dp-highlight-color: rgb(25 118 210 / 10%);
	--dp-range-between-dates-background-color: var(--dp-hover-color, #f3f4f8);
	--dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
	--dp-range-between-border-color: var(--dp-hover-color, #f3f4f8);
}

:root {
	--dp-font-family: 'noto-sans', -apple-system, blinkmacsystemfont, 'Segoe UI', roboto, oxygen, ubuntu, cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
	--dp-border-radius: 8px;
	--dp-cell-border-radius: 5px;
	--dp-common-transition: all 0.1s ease-in;

	/*Sizing*/
	--dp-button-height: 35px;
	--dp-month-year-row-height: 35px;
	--dp-month-year-row-button-size: 35px;
	--dp-button-icon-height: 20px;
	--dp-cell-size: 40px;
	--dp-cell-padding: 10px;
	--dp-common-padding: 10px;
	--dp-input-icon-padding: 35px;
	--dp-input-padding: 12px 16px;
	--dp-menu-min-width: 260px;
	--dp-action-buttons-padding: 2px 5px;
	--dp-row-margin: 5px 0;
	--dp-calendar-header-cell-padding: 0.5rem;
	--dp-two-calendars-spacing: 10px;
	--dp-overlay-col-padding: 3px;
	--dp-time-inc-dec-button-size: 32px;
	--dp-menu-padding: 6px 8px;

	/*Font sizes*/
	--dp-font-size: 0.875rem;
	--dp-preview-font-size: 0.8rem;
	--dp-time-font-size: 0.8rem;

	/*Transitions*/
	--dp-animation-duration: 0.1s;
	--dp-menu-appear-transition-timing: cubic-bezier(0.4, 0, 1, 1);
	--dp-transition-timing: ease-out;
}

.dp__input {
	@apply w-full grow rounded-none border border-crowns-grey bg-crowns-dark-grey px-2 py-3 text-sm normal-case placeholder:normal-case placeholder:text-crowns-castle-stone focus:border-[#2c2c2c] focus:outline-none;
}

.dp__main {
	@apply w-full;
}

.dp__input_wrap {
	@apply w-full;
}

.dp__input_wrap input {
	@apply w-full grow border border-crowns-grey bg-crowns-dark-grey px-2 py-3 pl-9 text-sm normal-case placeholder:normal-case placeholder:text-crowns-castle-stone focus:border-[#2c2c2c] focus:outline-none;
}
.dp__overlay_cell_disabled {
	color: var(--dp-disabled-color-text);
	&:hover {
		color: var(--dp-disabled-color-text);
	}
}
</style>
